import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import { ToastError } from 'src/components/atoms/toast/toast-error';
import { CommissionWithRelations } from 'src/models/commission.model';
import { getCommission, refreshCommissionRelations, refreshCommissions } from './commissions.reducer';
import { closeModal } from './modal.reducer';

type InitialState = {
  loadingRoles: boolean;
  failureRoles: boolean;
  successRoles: boolean;
  commissionWithRolesUpdated: CommissionWithRelations;
  loadingUsers: boolean;
  failureUsers: boolean;
  successUsers: boolean;
  commissionWithUsersUpdated: CommissionWithRelations;
  loadingGlobal: boolean;
  failureGlobal: boolean;
  successGlobal: boolean;
  commissionWithGlobalUpdated: CommissionWithRelations;
};

const INITIAL_STATE: InitialState = {
  loadingRoles: false,
  failureRoles: false,
  successRoles: false,
  commissionWithRolesUpdated: null as unknown as CommissionWithRelations,
  loadingUsers: false,
  failureUsers: false,
  successUsers: false,
  commissionWithUsersUpdated: null as unknown as CommissionWithRelations,
  loadingGlobal: false,
  failureGlobal: false,
  successGlobal: false,
  commissionWithGlobalUpdated: null as unknown as CommissionWithRelations,
};

export const updateCommissionRoles = createAsyncThunk(
  'commission/roles/put',
  async (data: { roleId: number; commissionId: number }, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<CommissionWithRelations> = await axios.put(`/api/commission/roles`, data);

      dispatch(
        getCommission({
          commissionId: data.commissionId,
        })
      );
      dispatch(closeModal());

      return response.data;
    } catch (error: any) {
      toast(() => ToastError(error.message));
      return rejectWithValue(error.message);
    }
  }
);

export const updateCommissionUsers = createAsyncThunk(
  'commission/users/put',
  async (data: { userId: number; commissionId: number }, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<CommissionWithRelations> = await axios.put(`/api/commission/users`, data);

      dispatch(refreshCommissionRelations());
      dispatch(closeModal());

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateCommissionGlobal = createAsyncThunk(
  'commission/global/put',
  async (data: { commissionId: number }, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<CommissionWithRelations> = await axios.put(`/api/commission/global`, data);

      dispatch(refreshCommissions());
      dispatch(closeModal());

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateCommissionRelationsSlice = createSlice({
  name: 'update/commissions-relations',
  initialState: INITIAL_STATE,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(updateCommissionRoles.pending, (state: InitialState) => {
      return {
        ...state,
        loadingRoles: true,
        failureRoles: false,
        successRoles: false,
      };
    });
    builder.addCase(updateCommissionRoles.fulfilled, (state: InitialState, action: PayloadAction<CommissionWithRelations>) => {
      return {
        ...state,
        loadingRoles: false,
        failureRoles: false,
        successRoles: true,
        commissionWithRolesUpdated: action.payload,
      };
    });
    builder.addCase(updateCommissionRoles.rejected, (state: InitialState) => {
      return {
        ...state,
        loadingRoles: false,
        failureRoles: true,
        successRoles: false,
      };
    });
    builder.addCase(updateCommissionUsers.pending, (state: InitialState) => {
      return {
        ...state,
        loadingUsers: true,
        failureUsers: false,
        successUsers: false,
      };
    });
    builder.addCase(updateCommissionUsers.fulfilled, (state: InitialState, action: PayloadAction<CommissionWithRelations>) => {
      return {
        ...state,
        loadingUsers: false,
        failureUsers: false,
        successUsers: true,
        commissionWithUsersUpdated: action.payload,
      };
    });
    builder.addCase(updateCommissionUsers.rejected, (state: InitialState) => {
      return {
        ...state,
        loadingUsers: false,
        failureUsers: true,
        successUsers: false,
      };
    });
    builder.addCase(updateCommissionGlobal.pending, (state: InitialState) => {
      return {
        ...state,
        loadingGlobal: true,
        failureGlobal: false,
        successGlobal: false,
      };
    });
    builder.addCase(updateCommissionGlobal.fulfilled, (state: InitialState, action: PayloadAction<CommissionWithRelations>) => {
      return {
        ...state,
        loadingGlobal: false,
        failureGlobal: false,
        successGlobal: true,
        commissionWithGlobalUpdated: action.payload,
      };
    });
    builder.addCase(updateCommissionGlobal.rejected, (state: InitialState) => {
      return {
        ...state,
        loadingGlobal: false,
        failureGlobal: true,
        successGlobal: false,
      };
    });
  },
});

export default updateCommissionRelationsSlice.reducer;
